import React, { useEffect, useState } from 'react';
import { List, Typography, Empty, Select, Space, Row, Col, Card, App } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import { mutations, queries } from '../../graphql/graphql';
import { Loader } from '../UI/Loader';
import { IGenerationTask } from '../../types/generationTask';
import { IPlanPhoto } from '../../types/plans';
import GenerationTaskItem from './GenerationTaskItem';
import axios from 'axios';
import { getFileExtension } from '../../utils/utils';

const { Title, Text } = Typography;
const { Option } = Select;

interface GenerationTaskListProps {
  planId?: string;
  modelType?: string;
  refreshPlanData: () => void;
  upscaleImage?: (task: IGenerationTask) => void;
  onUseImage?: (imageUrl: string) => void;
  photos?: IPlanPhoto[];
}

const GenerationTaskList: React.FC<GenerationTaskListProps> = ({
  planId,
  modelType,
  refreshPlanData,
  upscaleImage,
  onUseImage,
  photos = [],
}) => {
  const { message } = App.useApp();
  const [filter, setFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [disabled, setDisabled] = useState<boolean>(false);

  const { data, loading, error, refetch } = useQuery(
    queries.generationTasks.GET_GENERATION_TASKS_BY_PLAN_ID,
    {
      variables: { planId },
      skip: !planId,
    }
  );
  const [SAVE_RESULT_TO_PLAN] = useMutation(mutations.generationTasks.SAVE_RESULT_TO_PLAN);
  const [RETRY_GENERATION_TASK] = useMutation(mutations.generationTasks.RETRY_GENERATION_TASK);

  useEffect(() => {
    // @TODO REMOVE WHEN ADD SUBSCRIPTION Graphql
    let intervalId: NodeJS.Timeout | null = null;

    if (planId) {
      intervalId = setInterval(() => {
        refetch();
        console.log('update list');
      }, 2500);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [planId, refetch]);

  if (loading) return <Loader />;
  if (error) return <Text type="danger">Error loading tasks: {error.message}</Text>;
  if (!data || !data.getGenerationTasksByPlanId.length) {
    return (
      <Card style={{ padding: '24px', textAlign: 'center' }}>
        <Empty description="No generation tasks found" />
      </Card>
    );
  }

  const items: IGenerationTask[] = data.getGenerationTasksByPlanId;

  const filteredByModel = modelType ? items.filter(item => item.input.model === modelType) : items;

  const filteredItems =
    filter === 'all' ? filteredByModel : filteredByModel.filter(item => item.status === filter);

  const onSave = async (taskId: string) => {
    setDisabled(true);
    const result = await SAVE_RESULT_TO_PLAN({
      variables: {
        planId,
        taskId,
      },
    });
    if (result.data.saveResultToPlan) {
      message.success('Image saved to plan successfully');
      refreshPlanData();
    }
    setDisabled(false);
  };

  const onUseIt = async (taskId: string) => {
    setDisabled(true);
    const task = items.find(item => item.id === taskId);

    if (task && task.output?.result) {
      const imageUrl = task.output.result;
      const imageExists = photos.some(photo => photo.original === imageUrl);

      if (!imageExists) {
        const result = await SAVE_RESULT_TO_PLAN({
          variables: {
            planId,
            taskId,
          },
        });
        if (result.data.saveResultToPlan) {
          message.success('Image saved to plan successfully');
          refreshPlanData();
        }
      }

      if (onUseImage) {
        onUseImage(imageUrl);
        message.success('Image selected for generation');
      }
    }
    setDisabled(false);
  };

  const onRetry = async (taskId: string) => {
    setDisabled(true);
    const result = await RETRY_GENERATION_TASK({
      variables: {
        taskId,
        planId,
      },
    });
    if (result.data.retryGenerationTask) {
      message.success('Task retried successfully');
      refetch();
    }
    setDisabled(false);
  };

  const onDownload = async (url: string) => {
    try {
      setDisabled(true);
      const response = await axios.post(
        '/api/download/image',
        { url },
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const contentType = response.headers['content-type'];
      const extension = getFileExtension(contentType);
      const blob = new Blob([response.data], { type: contentType });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${planId}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download error:', error);
      message.error(
        `Error downloading photo: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setDisabled(false);
    }
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            Generation Tasks {modelType ? `(${modelType})` : ''}
          </Title>
        </Col>
        <Col>
          <Space>
            <Text>Filter by status:</Text>
            <Select value={filter} onChange={setFilter} style={{ width: 120 }}>
              <Option value="all">All</Option>
              <Option value="created">Created</Option>
              <Option value="pending">Pending</Option>
              <Option value="done">Done</Option>
              <Option value="failure">Failure</Option>
            </Select>
          </Space>
        </Col>
      </Row>

      <List
        dataSource={filteredItems}
        pagination={{
          onChange: page => {
            setCurrentPage(page);
            console.log(page);
          },
          current: currentPage,
          pageSize: pageSize,
          position: items.length > pageSize ? 'both' : 'bottom',
          showSizeChanger: true,
          responsive: true,
          showLessItems: false,
          onShowSizeChange: (_, size) => {
            setPageSize(size);
          },
        }}
        renderItem={item => (
          <List.Item key={item.id}>
            <GenerationTaskItem
              task={item}
              onRetry={onRetry}
              onDownload={onDownload}
              onSave={onSave}
              onUseIt={onUseImage ? onUseIt : undefined}
              disabled={disabled}
              upscaleImage={upscaleImage}
            />
          </List.Item>
        )}
      />
    </div>
  );
};

export default GenerationTaskList;
